import apiClient from "./api";

/**
 * Customer Access Service
 * Handles all API calls related to customer access and selection
 * Following the same pattern as AuthService
 */
class CustomerAccessService {
  /**
   * Handle axios error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data;

      if (error.response.status === 400 && data.errors) {
        // Validation errors
        const errorMessage = data.errors.map((err) => err.msg).join(", ");
        throw new Error(errorMessage);
      }

      if (error.response.status === 403) {
        throw new Error("Access denied to this customer");
      }

      throw new Error(data.message || "An error occurred");
    } else if (error.request) {
      // Request made but no response
      throw new Error(
        "Cannot connect to the server. Please ensure the backend is running."
      );
    } else {
      // Something else happened
      throw new Error(error.message || "An error occurred");
    }
  }

  /**
   * Get all customers accessible to the current user
   * @returns {Promise<Array>} List of accessible customers
   */
  async getAccessibleCustomers() {
    try {
      const response = await apiClient.post("/api/customer-access/accessible");

      const data = response.data;

      if (data.success && data.data) {
        return data.data;
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Get accessible customers error:", error);
      this.handleError(error);
    }
  }

  /**
   * Get primary customer for auto-redirect (simple users)
   * @returns {Promise<Object|null>} Primary customer or null
   */
  async getPrimaryCustomer() {
    try {
      const response = await apiClient.post("/api/customer-access/primary");

      const data = response.data;

      if (data.success && data.data) {
        return data.data;
      }

      // No primary customer found
      return null;
    } catch (error) {
      console.error("Get primary customer error:", error);
      // Return null instead of throwing for graceful handling
      return null;
    }
  }

  /**
   * Validate if user has access to a specific customer
   * @param {string} customerId - Customer ID to validate
   * @returns {Promise<boolean>} True if user has access
   */
  async validateCustomerAccess(customerId) {
    try {
      const response = await apiClient.post("/api/customer-access/validate", {
        customerId,
      });

      const data = response.data;

      if (data.success) {
        return data.hasAccess === true;
      }

      return false;
    } catch (error) {
      console.error("Validate customer access error:", error);
      return false;
    }
  }

  /**
   * Get customer by ID
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object>} Customer data
   */
  async getCustomerById(customerId) {
    try {
      const response = await apiClient.post("/api/customers/get", {
        customerId,
      });

      const data = response.data;

      if (data.success && data.data && data.data.customer) {
        return data.data.customer;
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Get customer error:", error);
      this.handleError(error);
    }
  }

  // ========== CONSULTANT MANAGEMENT ==========

  /**
   * Get all consultants assigned to a customer
   * @param {string} customerId - Customer ID
   * @returns {Promise<Array>} List of consultants
   */
  async getCustomerConsultants(customerId) {
    try {
      const response = await apiClient.get(
        `/api/customer-access/${customerId}/consultants`
      );

      const data = response.data;

      if (data.success && data.data) {
        return data.data;
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Get customer consultants error:", error);
      this.handleError(error);
    }
  }

  /**
   * Assign a consultant to a customer
   * @param {string} consultantId - Consultant user ID
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object>} Assignment data
   */
  async assignConsultant(consultantId, customerId) {
    try {
      const response = await apiClient.post(
        "/api/customer-access/assign-consultant",
        { consultantId, customerId }
      );

      const data = response.data;

      if (data.success) {
        return data.data;
      }

      throw new Error(data.message || "Failed to assign consultant");
    } catch (error) {
      console.error("Assign consultant error:", error);
      this.handleError(error);
    }
  }

  /**
   * Remove a consultant from a customer
   * @param {string} consultantId - Consultant user ID
   * @param {string} customerId - Customer ID
   * @returns {Promise<void>}
   */
  async removeConsultant(consultantId, customerId) {
    try {
      const response = await apiClient.post(
        "/api/customer-access/remove-consultant",
        { consultantId, customerId }
      );

      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || "Failed to remove consultant");
      }
    } catch (error) {
      console.error("Remove consultant error:", error);
      this.handleError(error);
    }
  }

  // ========== OWNER MANAGEMENT ==========

  /**
   * Set/update/remove customer owner
   * @param {string} customerId - Customer ID
   * @param {string|null} ownerId - Owner user ID (null to remove owner)
   * @returns {Promise<Object>} Updated customer data
   */
  async setCustomerOwner(customerId, ownerId) {
    try {
      const response = await apiClient.post("/api/customer-access/set-owner", {
        customerId,
        ownerId: ownerId || null,
      });

      const data = response.data;

      if (data.success && data.data) {
        return data.data;
      }

      throw new Error(data.message || "Failed to set owner");
    } catch (error) {
      console.error("Set customer owner error:", error);
      this.handleError(error);
    }
  }
}

// Export a singleton instance
const customerAccessService = new CustomerAccessService();
export default customerAccessService;
